import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "../../lib/api-client";
import { shortId } from "../../lib/format";
import type { components } from "../../lib/api-types.gen";

// Contract-derived (ADR-S1-005). GET /users returns the active users plus their
// roles; it grounds both name resolution (UUID -> displayName) and the
// owner/assignee pickers on the detail screen.
export type UserSummary = components["schemas"]["UserSummary"];

// The directory changes rarely; keep it fresh a few minutes to avoid refetch
// churn while the incident screens mount and unmount (mirrors master-data).
const USERS_STALE = 5 * 60_000;

/** GET /users - active users, ordered by display name (server-side). */
export function useUsers() {
  return useQuery({
    queryKey: ["users"],
    queryFn: () => apiFetch<UserSummary[]>("/users"),
    staleTime: USERS_STALE,
  });
}

/** Build an id -> UserSummary map for O(1) name resolution on list/detail. */
export function toUserMap(
  list: readonly UserSummary[] | undefined,
): Map<string, UserSummary> {
  return new Map((list ?? []).map((user) => [user.id, user]));
}

/**
 * Resolve a person id to a display name, falling back to the short UUID form
 * when the id is null/undefined or the user is not in the (active-only)
 * directory - e.g. a historical author whose account is now inactive.
 */
export function userName(
  id: string | null | undefined,
  map: Map<string, UserSummary>,
): string {
  if (!id) return shortId(id);
  return map.get(id)?.displayName ?? shortId(id);
}

/** Active entries only - the pool offered in the owner/assignee pickers. */
export function activeUsers(
  list: readonly UserSummary[] | undefined,
): UserSummary[] {
  return (list ?? []).filter((user) => user.active);
}
